import { Box, Typography, Paper, Grid, Card, CardContent, Avatar, Chip, Button, List, ListItem, ListItemAvatar, ListItemText, Divider, Container } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useAppSelector } from '../hooks/redux'
import { useNavigate } from 'react-router-dom'
import {
  Folder,
  Groups,
  People,
  Description,
  Assessment,
  VideoCall,
  TrendingUp,
  Notifications,
  Schedule,
  ArrowForward,
  AttachFile,
  LibraryBooks,
  BarChart,
  CalendarToday,
  AccessTime,
} from '@mui/icons-material'
import { gradientBrand } from '../theme/brand'

// Dummy data
const dummyStats = {
  totalCases: 12,
  totalTeams: 48,
  totalParticipants: 192,
  totalMemorials: 96,
  totalScores: 72,
  averageScore: 82.5,
  activeCourtrooms: 3,
}

const dummyRecentActivities = [
  { id: '1', type: 'memorial', title: 'Đội Luật sư Xuất sắc đã nộp memorial', time: '2 giờ trước', case: 'Cuộc thi Pháp luật Dân sự 2024' },
  { id: '2', type: 'score', title: 'Giám khảo đã chấm điểm cho Đội Tư vấn Pháp lý', time: '4 giờ trước', case: 'Cuộc thi Pháp luật Thương mại 2024' },
  { id: '3', type: 'courtroom', title: 'Virtual Courtroom đã được tạo cho vòng tranh tụng', time: '6 giờ trước', case: 'Cuộc thi Pháp luật Dân sự 2024' },
  { id: '4', type: 'team', title: 'Đội Chuyên gia Pháp luật đã đăng ký tham gia', time: '1 ngày trước', case: 'Cuộc thi Pháp luật Hình sự 2023' },
  { id: '5', type: 'evidence', title: 'Đội Nghiên cứu Pháp lý đã upload chứng cứ', time: '1 ngày trước', case: 'Cuộc thi Pháp luật Dân sự 2024' },
]

const dummyUpcomingCourtrooms = [
  { id: '1', case: 'Cuộc thi Pháp luật Dân sự 2024', round: 'Vòng tranh tụng online', date: '2025-01-28', time: '10:00', status: 'scheduled' },
  { id: '2', case: 'Cuộc thi Pháp luật Thương mại 2024', round: 'Vòng tranh tụng online', date: '2025-01-29', time: '14:00', status: 'scheduled' },
  { id: '3', case: 'Cuộc thi Pháp luật Hình sự 2023', round: 'Vòng tranh tụng online', date: '2025-01-30', time: '09:00', status: 'scheduled' },
]

const dummyTopTeams = [
  { rank: 1, name: 'Đội Luật sư Xuất sắc', case: 'Cuộc thi Pháp luật Dân sự 2024', score: 95.5 },
  { rank: 2, name: 'Đội Tư vấn Pháp lý', case: 'Cuộc thi Pháp luật Thương mại 2024', score: 93.2 },
  { rank: 3, name: 'Đội Chuyên gia Pháp luật', case: 'Cuộc thi Pháp luật Dân sự 2024', score: 91.8 },
]

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'memorial':
      return <Description color="primary" />
    case 'score':
      return <Assessment color="success" />
    case 'courtroom':
      return <VideoCall color="info" />
    case 'team':
      return <Groups color="secondary" />
    case 'evidence':
      return <AttachFile color="warning" />
    default:
      return <Notifications color="action" />
  }
}

export default function HomePage() {
  const { user } = useAppSelector((state) => state.auth)
  const navigate = useNavigate()

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Quản trị viên',
      secretary: 'Ban Thư ký',
      judge: 'Giám khảo',
      student: 'Thí sinh',
      coach: 'Huấn luyện viên',
      support: 'Hỗ trợ kỹ thuật',
    }
    return labels[role] || role
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      {/* Welcome — thẻ hero đồng bộ nhận diện */}
      <Box
        sx={(theme) => ({
          mb: 4,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.08)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            background: gradientBrand,
            borderRadius: '3px 0 0 3px',
          },
        })}
      >
        <Box sx={{ pl: { xs: 0.5, sm: 1 } }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.65rem', sm: '2.125rem' }, color: 'primary.main' }}>
            Chào mừng đến với E-Court Platform
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                Xin chào, <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>{user.full_name || user.username}</Box>
              </Typography>
              <Chip label={getRoleLabel(user.role)} color="secondary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
            </Box>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1.25, maxWidth: '42rem', lineHeight: 1.65 }}>
            Hệ thống quản lý và tổ chức cuộc thi tranh tụng điện tử — giao diện thân thiện, luồng làm việc rõ ràng.
          </Typography>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              borderTop: '4px solid',
              borderTopColor: 'primary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.primary.main, 0.12)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số cuộc thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {dummyStats.totalCases}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#144E8C', 0.12), color: 'primary.main', width: 56, height: 56 }}>
                  <Folder />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              borderTop: '4px solid',
              borderTopColor: 'secondary.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.secondary.main, 0.15)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số đội thi
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">
                    {dummyStats.totalTeams}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: alpha('#F64183', 0.12), color: 'secondary.main', width: 56, height: 56 }}>
                  <Groups />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'success.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.success.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Tổng số thí sinh
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {dummyStats.totalParticipants}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: 'success.dark', width: 56, height: 56 }}>
                  <People />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={(theme) => ({
              height: '100%',
              border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
              borderTop: '4px solid',
              borderTopColor: 'info.main',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: `0 14px 36px ${alpha(theme.palette.info.main, 0.14)}`,
                transform: 'translateY(-2px)',
              },
            })}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                    Điểm trung bình
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {dummyStats.averageScore}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.info.main, 0.12), color: 'info.dark', width: 56, height: 56 }}>
                  <TrendingUp />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <ArrowForward color="secondary" /> Truy cập nhanh
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Folder />}
                onClick={() => navigate('/cases')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Cuộc thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Groups />}
                onClick={() => navigate('/teams')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Đội thi
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Description />}
                onClick={() => navigate('/memorials')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Memorial
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Assessment />}
                onClick={() => navigate('/scoring')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Chấm điểm
              </Button>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<VideoCall />}
                onClick={() => navigate('/courtroom')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Virtual Courtroom
              </Button>
              {(user?.role === 'secretary' || user?.role === 'support') && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<BarChart />}
                  onClick={() => navigate('/reports')}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                >
                  Báo cáo và Phân tích
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<LibraryBooks />}
                onClick={() => navigate('/knowledge')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
              >
                Kho học liệu
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              p: 3,
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
            })}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
              <Notifications color="secondary" /> Hoạt động gần đây
            </Typography>
            <List sx={{ p: 0 }}>
              {dummyRecentActivities.map((activity, idx) => (
                <Box key={activity.id}>
                  <ListItem sx={{ px: 0, py: 1.5 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}>
                        {getActivityIcon(activity.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {activity.title}
                        </Typography>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                            {activity.case}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block', mt: 0.5 }}>
                            {activity.time}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {idx < dummyRecentActivities.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Upcoming Courtrooms & Top Teams */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Upcoming Courtrooms */}
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <Schedule color="secondary" /> Sắp diễn ra
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dummyUpcomingCourtrooms.map((courtroom) => (
                  <Box
                    key={courtroom.id}
                    sx={(theme) => ({
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      border: '1px solid',
                      borderColor: 'divider',
                    })}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {courtroom.case}
                      </Typography>
                      <Chip label={courtroom.status} size="small" color="info" />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {courtroom.round}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 14, color: 'primary.main' }} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(courtroom.date).toLocaleDateString('vi-VN')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14, color: 'secondary.main' }} />
                        <Typography variant="caption" color="text.secondary">
                          {courtroom.time}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
              <Button
                fullWidth
                variant="text"
                color="primary"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/courtroom')}
                sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Xem tất cả
              </Button>
            </Paper>

            {/* Top Teams */}
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 3,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 8px 28px ${alpha(theme.palette.primary.main, 0.06)}`,
              })}
            >
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                <TrendingUp color="secondary" /> Top 3 đội thi
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dummyTopTeams.map((team) => (
                  <Box
                    key={team.rank}
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor:
                        team.rank === 1 ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.action.hover, 0.5),
                      border: team.rank === 1 ? `1px solid ${alpha(theme.palette.secondary.main, 0.35)}` : '1px solid transparent',
                    })}
                  >
                    <Chip
                      label={team.rank}
                      color={team.rank === 1 ? 'primary' : 'default'}
                      sx={{ fontWeight: 'bold', minWidth: 40 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {team.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {team.case}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="primary.main">
                      {team.score}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  )
}
